/**
 * RecycleBinTab — soft-delete recovery UI for room admins.
 *
 * Purpose
 * -------
 * Every ``DELETE /rooms/{id}/documents/{doc_id}`` on the backend is a
 * SOFT delete — the document row stays in Mongo with ``deleted_at`` +
 * ``deleted_by``. GridFS blob + PDF cache also stay intact so the file
 * can be restored bit-for-bit within the 30-day retention window. A
 * background reaper permanently purges items older than 30 days.
 *
 * This tab exposes the bin to admins with two actions per row:
 *   • Restore — clears ``deleted_at``; parent-folder-lost items go
 *     back to the room root.
 *   • Delete permanently — hard-delete via /documents/{id}/purge.
 *     Confirm dialog because this is irreversible.
 *
 * Row shape mirrors the DocumentsTab file listing so admins get
 * muscle-memory continuity: icon · name · type chip · deleted-by ·
 * deleted-at · days-remaining chip · action buttons.
 */
import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Trash2, RefreshCw, Undo2, AlertTriangle, Folder, FileText,
} from "lucide-react";

function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function daysChipTone(days) {
  if (days <= 3) return "bg-rose-500/10 text-rose-500 border-rose-500/30";
  if (days <= 7) return "bg-amber-500/10 text-amber-500 border-amber-500/30";
  return "bg-emerald-500/10 text-emerald-500 border-emerald-500/30";
}

export default function RecycleBinTab({ roomId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // {id, action} to disable per-row buttons
  const [confirm, setConfirm] = useState(null); // {id, name}

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/rooms/${roomId}/recycle-bin`);
      setItems(data.items || []);
    } catch (e) {
      toast.error("Couldn't load Recycle Bin");
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const restore = async (it) => {
    setBusy({ id: it.id, action: "restore" });
    try {
      await api.post(`/rooms/${roomId}/documents/${it.id}/restore`);
      toast.success(`Restored "${it.name}"`);
      setItems((cur) => cur.filter((x) => x.id !== it.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    } finally {
      setBusy(null);
    }
  };

  const purge = async (it) => {
    setBusy({ id: it.id, action: "purge" });
    try {
      await api.delete(`/rooms/${roomId}/documents/${it.id}/purge`);
      toast.success(`Permanently deleted "${it.name}"`);
      setItems((cur) => cur.filter((x) => x.id !== it.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(null);
      setConfirm(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="recycle-bin-tab">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono text-muted-foreground inline-flex items-center gap-2">
            <Trash2 className="w-3.5 h-3.5" /> — Recycle bin
          </div>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Deleted files and folders stay here for <strong>30 days</strong> before
            being permanently removed. Anyone can be restored during that window.
          </p>
        </div>
        <button
          onClick={fetchItems}
          data-testid="recycle-bin-refresh"
          className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-md border border-border hover:bg-secondary transition"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="border border-border rounded-lg bg-card overflow-hidden">
        {loading && !items.length && (
          <div className="p-8 text-sm text-muted-foreground text-center">Loading…</div>
        )}
        {!loading && !items.length && (
          <div className="p-10 text-center" data-testid="recycle-bin-empty">
            <Trash2 className="w-10 h-10 mx-auto text-muted-foreground opacity-50" />
            <div className="mt-3 text-sm">Recycle bin is empty.</div>
            <div className="mt-1 label-mono text-muted-foreground">
              Deleted files land here — you have 30 days to restore anything you didn&apos;t
              mean to delete.
            </div>
          </div>
        )}
        {!!items.length && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-border/60 bg-secondary/30">
                <th className="px-4 py-2 label-mono text-muted-foreground">Name</th>
                <th className="px-4 py-2 label-mono text-muted-foreground">Deleted by</th>
                <th className="px-4 py-2 label-mono text-muted-foreground">Deleted</th>
                <th className="px-4 py-2 label-mono text-muted-foreground">Retention</th>
                <th className="px-4 py-2 label-mono text-muted-foreground text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const Icon = it.type === "folder" ? Folder : FileText;
                const isBusy = busy?.id === it.id;
                return (
                  <tr
                    key={it.id}
                    data-testid={`recycle-row-${it.id}`}
                    className="border-b border-border/40 last:border-b-0 hover:bg-secondary/40 transition align-middle"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <Icon className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                        <span className="truncate line-through text-muted-foreground" title={it.name}>
                          {it.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{it.deleted_by_name || "—"}</td>
                    <td className="px-4 py-3 label-mono whitespace-nowrap" title={it.deleted_at}>
                      {fmtWhen(it.deleted_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border mono text-[10px] uppercase tracking-wider ${daysChipTone(it.days_remaining)}`}>
                        {it.days_remaining} day{it.days_remaining === 1 ? "" : "s"} left
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => restore(it)}
                        disabled={isBusy}
                        data-testid={`recycle-restore-${it.id}`}
                        title="Restore"
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-500 hover:bg-emerald-500/10 mono uppercase tracking-wider transition disabled:opacity-50"
                      >
                        <Undo2 className="w-3 h-3" /> Restore
                      </button>
                      <button
                        onClick={() => setConfirm({ id: it.id, name: it.name })}
                        disabled={isBusy}
                        data-testid={`recycle-purge-${it.id}`}
                        title="Delete permanently"
                        className="ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-rose-500/30 bg-rose-500/[0.06] text-rose-500 hover:bg-rose-500/10 mono uppercase tracking-wider transition disabled:opacity-50"
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Purge confirm — destructive so we ask twice. */}
      {confirm && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm" data-testid="recycle-purge-confirm">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-rose-500/10 text-rose-500 grid place-items-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-lg tracking-tight">Delete permanently?</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  &quot;<span className="font-medium text-foreground">{confirm.name}</span>&quot; and
                  any child folders/files will be permanently deleted. This action
                  cannot be undone.
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setConfirm(null)}
                data-testid="recycle-purge-cancel"
                className="px-3 py-1.5 text-sm rounded-md border border-border hover:bg-secondary transition"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const it = items.find((x) => x.id === confirm.id);
                  if (it) purge(it);
                }}
                data-testid="recycle-purge-confirm-btn"
                className="px-3 py-1.5 text-sm rounded-md bg-rose-500 text-white hover:bg-rose-600 transition"
              >
                Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
